import React from "react";
import * as Select from "@radix-ui/react-select";
import { useEffect, useMemo, useState } from "react";
import { Arrow } from "../SVGs/Arrow";
import { motion, AnimatePresence } from "framer-motion";

type OptionsType = {
  id: string | number;
  label: string;
};

type SelectType = {
  options: OptionsType[];
  backgroundColor?: string;
  defaultValue?: string;
  onChange: (id: number | string) => void;
  className?: string;
  position?: "popper" | "item-aligned";
  customValue?: string;
  persistBackgroundColor?: boolean;
};

export function SelectComponent({
  options,
  backgroundColor,
  onChange,
  defaultValue,
  position,
  className,
  customValue,
  persistBackgroundColor,
}: SelectType): JSX.Element {
  const [value, setValue] = useState<string | number | boolean>("");
  const [isOpen, setIsOpen] = useState(false);
  const [outsideClick, setOutsideClick] = useState(false);
  /* TODO:
   * 1. Rewrite component not using Radix select, does not offer functionality needed.
   * 2. Temporary overlay to prevent Radix clicking behind dropdown,
   * see many GitHub issues such as: https://github.com/radix-ui/primitives/pull/2085
   */
  const [delayOverlay, setDelayOverlay] = useState(false);

  const overlayDelay = (e?: CustomEvent) => {
    // Check if user clicks outside select element
    if (typeof document !== "undefined") {
      if (e?.target === document?.documentElement) {
        e.preventDefault();
        setIsOpen(false);
      }
      setTimeout(() => {
        setDelayOverlay(false);
      }, 110);
    }
  };

  const handleOpenChange = (open: boolean) => {
    setIsOpen(open);
    setDelayOverlay(true);

    if (!open) {
      overlayDelay();
      let reselectedItem: boolean;
      if (value === options?.[0]?.id) {
        const updatedValue = (() => {
          setValue(prevValue => {
            const determineValue =
              prevValue === options?.[0]?.id ? true : false;
            reselectedItem = determineValue;
            return determineValue;
          });
        })();
      }
      setTimeout(() => {
        if (reselectedItem && window?.location?.hash && !outsideClick) {
          if (typeof document !== "undefined") {
            const targetElement = document?.querySelector(
              options?.[0]?.id as string
            );

            if (targetElement) {
              window.location.hash = options?.[0]?.id as string;
              targetElement.scrollIntoView({
                behavior: "smooth",
              });
              setValue(options?.[0]?.id);
            }
          }
        } else {
          setValue(value);
        }
      }, 100);

      if (
        window.location.hash === "" ||
        (window.location.hash === options?.[0]?.id && !outsideClick)
      ) {
        handleValueChange(options?.[0]?.id);
      }
    }
  };

  const handleValueChange = (newValue: string | number) => {
    setValue(newValue);
    onChange(newValue);

    setIsOpen(false);
  };

  useEffect(() => {
    setValue(defaultValue || options?.[0]?.id);
    if (window?.location.hash) {
      const hash = window?.location.hash;
      if (typeof document !== "undefined") {
        const targetElement = document?.querySelector(hash);

        if (targetElement) {
          targetElement.scrollIntoView({
            behavior: "smooth",
          });
        }
      }
    }
  }, [defaultValue]);

  const updateValue = useMemo(() => {
    overlayDelay();
    if (customValue) return customValue;

    return value?.toString();
  }, [customValue, value]);

  const classesTrigerOpen = `border-solid border-[1.5px] border-forest-900 ${
    !persistBackgroundColor && "!bg-white"
  } rounded-full text-left`;
  const classesArrowOpen = "!transform !rotate-0";

  return (
    <div className={`select-wrapper relative z-10 ${className}`}>
      <div
        className={`${
          delayOverlay ? "" : "hidden"
        } select-wrapper__overlay fixed top-0 left-0 z-[1000] opacity-0 w-full h-full bg-transparent`}
      ></div>
      <Select.Root
        defaultValue=""
        value={updateValue}
        onValueChange={handleValueChange}
        open={isOpen}
        onOpenChange={handleOpenChange}
      >
        <Select.Trigger
          className={`select-wrapper__trigger relative w-[335px] flex items-center outline-none rounded-full text-left h-[44px] p-4 font-semibold text-base	${
            isOpen && classesTrigerOpen
          }`}
          style={{ ...(backgroundColor && { backgroundColor }) }}
        >
          <div className="overflow-hidden h-8 flex items-center relative w-full">
            <AnimatePresence mode="wait">
              <motion.div
                key={updateValue}
                initial={{ y: "100%" }}
                animate={{ y: "0%" }}
                exit={{ y: "-100%" }}
                transition={{ duration: 0.35, ease: "easeInOut" }}
                className="w-full absolute select__font-set"
              >
                <Select.Value placeholder="">
                  <span className="select__font-set">
                    {options?.find(item => item.id === updateValue)?.label}
                  </span>
                </Select.Value>
              </motion.div>
            </AnimatePresence>
          </div>
          <Select.Icon>
            <Arrow
              className={`absolute right-5 w-5 top-[17px] transform rotate-180 ${
                isOpen && classesArrowOpen
              }`}
            />
          </Select.Icon>
        </Select.Trigger>
        <Select.Content
          onPointerDownOutside={overlayDelay}
          position="popper"
          className="select__open-select outline-none text-base font-medium text-forest-900 p-4 rounded-xl bg-white mt-2 border-[1.5px] border-forest-200 w-[335px]"
          style={{
            ...(persistBackgroundColor &&
              backgroundColor && {
                backgroundColor,
              }),
          }}
        >
          {options?.map((item, key) => (
            <Select.Item
              key={key}
              value={item.id?.toString()}
              textValue={item.id?.toString()}
              className="select__font-set pb-6 text-forest-900 text-base leading-none cursor-pointer outline-none last:pb-0"
            >
              <Select.ItemText>{item?.label}</Select.ItemText>
            </Select.Item>
          ))}
        </Select.Content>
      </Select.Root>
    </div>
  );
}

export default SelectComponent;
